// Versioned legend registry + evidence-gated route/color semantics.
//
// This module is the SINGLE SOURCE OF TRUTH for graph colours. The WebGL canvas
// and the accessible 2D summary both paint via paintGraph(), and the CI
// claim-governance tests assert against the SAME function — so it is impossible
// for cyan or green to render in the app without their exact backing record,
// because there is only one code path.
//
// Colour meanings (spec-exact):
//   gold  = normal / steady-state
//   red   = a registered stress event
//   cyan  = a solver route backed by an Evidence record carrying BOTH a real
//           solver_identity AND a real problem_id
//   green = an accepted route backed by a real Approval whose resource_digest
//           matches the accepted decision digest
//   blue  = a candidate (unverified)
//   white = the verified-classical candidate (selected, feasible, classical)
//   gray  = unavailable / unauthorized / filtered

export const CATEGORIES = ["gold", "red", "cyan", "green", "blue", "white", "gray"];

// Legend VERSIONS. A Run renders under the legend_version stamped on its contract,
// never the latest — advancing the live legend must not retro-change old Runs.
const LEGEND_V1 = {
  version: 1,
  colors: {
    gold: "#f5b942", red: "#ff4d5e", cyan: "#22d3ee", green: "#35d07f",
    blue: "#5b8cff", white: "#e8eef7", gray: "#6b7280",
  },
  meaningKey: {
    gold: "decisionGraph.legend.gold", red: "decisionGraph.legend.red",
    cyan: "decisionGraph.legend.cyan", green: "decisionGraph.legend.green",
    blue: "decisionGraph.legend.blue", white: "decisionGraph.legend.white",
    gray: "decisionGraph.legend.gray",
  },
};

// A DIFFERENT, later legend (meanings intentionally remapped) — used to PROVE that
// a Run stamped legend_version 1 still renders v1 meanings even when v2 is live.
const LEGEND_V2 = {
  version: 2,
  colors: {
    gold: "#eab308", red: "#dc2626", cyan: "#06b6d4", green: "#16a34a",
    blue: "#3b82f6", white: "#ffffff", gray: "#9ca3af",
  },
  meaningKey: LEGEND_V1.meaningKey,
};

export const LEGENDS = { 1: LEGEND_V1, 2: LEGEND_V2 };
// The latest legend the live app knows about. Runs do NOT auto-upgrade to this.
export const LIVE_LEGEND_VERSION = 2;

export function getLegend(version) {
  return LEGENDS[version] || LEGENDS[1];
}

// Detect the backing records once, from the canonical reconstruction + events.
export function buildSemantics({ recon, events }) {
  const summary = recon?.run?.summary || {};
  const evidence = recon?.evidence || [];
  const approvals = recon?.approvals || [];

  const solverEvidence = evidence.find(
    (e) => e && e.status === "verified" &&
      Array.isArray(e.lineage) &&
      e.lineage.some((l) => l && l.solver_identity && l.problem_id));

  const acceptedDigest = summary.accepted_resource_digest || null;
  const approval = acceptedDigest
    ? approvals.find((a) => a && a.resource_digest && a.resource_digest === acceptedDigest)
    : null;

  const stressEvent = (events || []).find(
    (e) => e && (e.metadata?.stress_event === true ||
      /partner_congestion_detected|alert_correlation_surge/.test(e.event_type || "")));

  return {
    hasSolverEvidence: !!solverEvidence,
    solverEvidence: solverEvidence || null,
    hasApproval: !!approval,
    approval: approval || null,
    hasStressEvent: !!stressEvent,
    stressEvent: stressEvent || null,
    acceptedDigest,
  };
}

// The ONE governance function. Returns { category, backedBy } for a route role.
// cyan/green are returned ONLY when their exact backing record is present.
export function classifyRoute(role, sem, node) {
  switch (role) {
    case "stress":
      return sem.hasStressEvent
        ? { category: "red", backedBy: { event_type: sem.stressEvent?.event_type } }
        : { category: "gray", backedBy: null };
    case "mobilize":
      return { category: "gold", backedBy: null };
    case "propose":
      return node?.verifiedClassical
        ? { category: "white", backedBy: { treatment: node?.label } }
        : { category: "blue", backedBy: null };
    case "compare-solver":
      if (sem.hasSolverEvidence) {
        const l = (sem.solverEvidence.lineage || []).find((x) => x && x.solver_identity && x.problem_id);
        return { category: "cyan", backedBy: { evidence_id: sem.solverEvidence.evidence_id, solver_identity: l.solver_identity, problem_id: l.problem_id } };
      }
      return { category: "gray", backedBy: null };
    case "compare-decision":
      return sem.hasApproval
        ? { category: "green", backedBy: { approval_id: sem.approval.approval_id, resource_digest: sem.approval.resource_digest } }
        : { category: "gray", backedBy: null };
    default:
      return { category: "gray", backedBy: null };
  }
}

// Paint the whole graph. Returns painted links (with category + backedBy + hex),
// respecting state gating (a route is 'gray' until its cinematic state arrives).
export function paintGraph({ model, semantics, state, legendVersion }) {
  const legend = getLegend(legendVersion);
  const stateFrom = { mobilize: 2, propose: 3, "compare-solver": 4, "compare-decision": 4 };
  const painted = (model?.links || []).map((link, i) => {
    const role = link.kind === "mobilize" ? "mobilize"
      : link.kind === "propose" ? "propose" : "compare-decision";
    // one of the compare links carries the solver-verification route (cyan)
    const effectiveRole = link.kind === "compare" && link.solverRoute ? "compare-solver" : role;
    const node = model.nodes.find((n) => n.id === link.target) || model.nodes.find((n) => n.id === link.source);
    let { category, backedBy } = classifyRoute(effectiveRole, semantics, node);
    const gatedFrom = stateFrom[effectiveRole] ?? 0;
    if (state < gatedFrom) { category = "gray"; backedBy = null; }
    return { index: i, a: link.a, b: link.b, kind: link.kind, role: effectiveRole, category, backedBy, hex: legend.colors[category] };
  });
  return painted;
}

// Assert-style helper reused by the CI governance test.
export function violatesClaimGovernance(painted) {
  return painted.filter((p) => {
    if (p.category === "cyan") return !(p.backedBy && p.backedBy.solver_identity && p.backedBy.problem_id);
    if (p.category === "green") return !(p.backedBy && p.backedBy.resource_digest);
    return false;
  });
}

// Agent aggregation for scale: cluster agents into buckets when there are many,
// so the viewport/inspector never tries to render thousands of individual nodes.
export function aggregateAgents(agentCount, maxClusters = 8) {
  const n = Math.max(0, agentCount | 0);
  if (n <= maxClusters) {
    return Array.from({ length: n }, (_, i) => ({ id: `agent-${i}`, count: 1, members: [i] }));
  }
  const clusters = [];
  const per = Math.ceil(n / maxClusters);
  for (let c = 0; c < maxClusters; c++) {
    const start = c * per;
    const end = Math.min(n, start + per);
    if (start >= end) break;
    clusters.push({ id: `agent-cluster-${c}`, count: end - start, members: Array.from({ length: end - start }, (_, k) => start + k) });
  }
  return clusters;
}

// Feasibility filter: infeasible treatments are EXCLUDED from the decision graph
// candidate nodes (but SolverComparison still lists them, marked infeasible).
export function feasibleCandidateTreatments(treatments) {
  return (treatments || []).filter(
    (t) => t && t.feasible !== false && t.engine_class !== "baseline" && t.objective_value != null);
}

const _fin = (v) => (typeof v === "number" && isFinite(v) ? v : null);

// QUANTUM REVEAL GOVERNANCE — the verified-D-Wave-lift reveal may ONLY light up
// when a REAL held-out-confirmation Evidence record backs it. A development
// signal (dev-only, not held-out confirmed) NEVER reveals a lift, and the
// absence of any lift Evidence is a plain "no lift" run. The summary flag alone
// can never trigger the reveal — the Evidence record is required (defense in
// depth, same pattern as the cyan/green route rule).
export function quantumRevealState(summary, evidence) {
  const list = evidence || [];
  const heldOut = list.find(
    (e) => e && e.status === "verified" && Array.isArray(e.lineage) &&
      e.lineage.some((l) => l && l.quantum_lift === true && l.held_out_confirmed === true && _fin(l.lift_pct) != null));
  if (heldOut) {
    const l = heldOut.lineage.find((x) => x && x.quantum_lift === true && x.held_out_confirmed === true && _fin(x.lift_pct) != null);
    return { state: "verified_lift", backedBy: { evidence_id: heldOut.evidence_id, lift_pct: l.lift_pct, held_out_confirmed: true } };
  }
  const dev = list.find(
    (e) => e && e.status === "verified" && Array.isArray(e.lineage) &&
      e.lineage.some((l) => l && l.quantum_lift === true && l.development_signal === true));
  if (dev) return { state: "development_signal", backedBy: { evidence_id: dev.evidence_id } };
  return { state: "no_lift", backedBy: null };
}

// Assert-style helper reused by the CI quantum-reveal governance test: a
// verified_lift reveal without a held-out-confirmed backing record is a violation.
export function violatesQuantumRevealGovernance(reveal) {
  if (reveal && reveal.state === "verified_lift") {
    return !(reveal.backedBy && reveal.backedBy.held_out_confirmed === true && _fin(reveal.backedBy.lift_pct) != null);
  }
  return false;
}

// The six required Quantum-state displays. Honest: the reveal is verified ONLY
// when a held-out-confirmed lift Evidence record exists; otherwise every value is
// "Unavailable" (dry-run gated) — never fabricated.
export function quantumDisplays(summary, evidence) {
  const reveal = quantumRevealState(summary, evidence);
  const verified = reveal.state === "verified_lift";
  const dwave = (summary?.treatments || []).find((t) => t && t.engine_class === "quantum");
  const gated = !verified;
  const liftPct = verified ? reveal.backedBy.lift_pct : null;
  return [
    { key: "objective", value: verified && dwave?.objective_value != null ? String(dwave.objective_value) : "Unavailable", gated },
    { key: "verified_lift", value: verified && liftPct != null ? `${liftPct}%` : "Unavailable", gated },
    { key: "chain_break", value: verified ? String(summary.chain_break_fraction ?? "Unavailable") : "Unavailable", gated },
    { key: "anneal_time", value: verified ? String(summary.anneal_time_us ?? "Unavailable") : "Unavailable", gated },
    { key: "embedding", value: verified ? String(summary.embedding_qubits ?? "Unavailable") : "Unavailable", gated },
    { key: "sampler_state", value: dwave?.state || (gated ? "dry_run_gated" : "Unavailable"), gated },
  ];
}

// Compute a MODELED predicted-savings projection from the run's own treatments:
// (current_policy objective − selected objective) / current_policy objective.
// This is a model projection, never a measurement — it is tagged 'modeled'.
function modeledPredictedSavings(s) {
  if (_fin(s.predicted_savings_pct) != null) return s.predicted_savings_pct;
  const treatments = s.treatments || [];
  const baseline = treatments.find((t) => t && t.source === "current_policy" && _fin(t.objective_value) != null);
  const selected = treatments.find((t) => t && t.selected && _fin(t.objective_value) != null);
  if (baseline && selected && baseline.objective_value !== 0) {
    return ((baseline.objective_value - selected.objective_value) / baseline.objective_value) * 100;
  }
  return null;
}

// REALIZED-OUTCOME GOVERNANCE (§4 Evidence vocabulary). Predicted savings is a
// MODELED projection (from the solver's objectives). REALIZED savings is
// 'measured' ONLY when a real measured Evidence record exists for this Run
// (a verified record carrying a numeric realized_savings_pct); otherwise it is
// 'unavailable' — NEVER a placeholder number. The delta is only computable, and
// only 'measured', when a real measured value exists.
export function realizedOutcome(summary, evidence) {
  const s = summary || {};
  const predictedVal = modeledPredictedSavings(s);
  const predicted = predictedVal != null
    ? { key: "predicted", value: predictedVal, basis: "modeled" }
    : { key: "predicted", value: null, basis: "unavailable" };

  const realizedRec = (evidence || []).find(
    (e) => e && e.status === "verified" && Array.isArray(e.lineage) &&
      e.lineage.some((l) => l && l.realized === true && _fin(l.realized_savings_pct) != null));
  const realizedL = realizedRec
    ? realizedRec.lineage.find((l) => l && l.realized === true && _fin(l.realized_savings_pct) != null)
    : null;
  const realized = realizedL
    ? { key: "realized", value: realizedL.realized_savings_pct, basis: "measured", evidence_id: realizedRec.evidence_id }
    : { key: "realized", value: null, basis: "unavailable" };

  const hasMeasured = realized.basis === "measured";
  const canDelta = hasMeasured && predicted.basis !== "unavailable";
  const delta = canDelta
    ? { key: "delta", value: realized.value - predicted.value, basis: "measured" }
    : { key: "delta", value: null, basis: "unavailable" };

  return { rows: [predicted, realized, delta], hasMeasured };
}
